import React, { useContext, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Link,
  IconButton,
  LinearProgress,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Breadcrumbs
} from "@mui/material";
import bkstApi from "../../common/api";
import currency from "currency.js";
import ConfigContext from "../../common/context/ConfigContext";
import { useNavigate } from "react-router";
import { getDataFromLocalStorage, getItemBasePrice, getItemMetadata, getItemUrl, getOptionListByOverride, getVariantTotal } from "../../common/util";
import ImageCarousel from "../../common/component/image/ImageCarousel";
import { AppStateContext } from "../../common/context/AppStateContext";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ProductDescription from "../component/ProductDescription";
import { Helmet } from "react-helmet";
import { Link as RouterLink } from "react-router-dom";
import ProductVariants from "../component/ProductVariants";

export default function ProductPage() {
  const siteConfig = useContext(ConfigContext);
  const { cart, updateCartState } = useContext(AppStateContext);

  let { productId, slug } = useParams();
  if (!productId && slug) {
    productId = slug.split("-").pop();
  }

  const navigate = useNavigate();

  const [product, setProduct] = useState("");
  const [variants, setVariants] = useState("");

  const [variant, setVariant] = useState("");

  const [quantity, setQuantity] = useState(1);
  const [variantPrice, setVariantPrice] = useState("");

  const [errors, setErrors] = useState([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    bkstApi(`/site/${siteConfig.id}/product/${productId}`).then((res) => {
      setProduct(res.data.product);
      setVariants({ ...res.data.product.variants });

      setDefaults(res.data.product);

      window.scrollTo(0, 0);
      setLoading(false);
    });
  }, [productId]);

  useEffect(() => {
    if (product) {
      // Create a deep copy to avoid mutating the original product.variants
      let tmp = JSON.parse(JSON.stringify(product.variants));
      for (const [key, value] of Object.entries(tmp)) {
        if (key === "size") {
          continue;
        }
        if (value.field.type === "select" && variant.size) {
          tmp[key].field.options = getOptionListByOverride(value.field.options, { size: variant.size.name });
          if (value.field.options.length === 0) {
            delete tmp[key];
          }
        }
      }
      setVariants(tmp);

      setVariantPrice(getVariantTotal(variant, getItemBasePrice(variant)));
    }
  }, [variant, quantity]);

  const validateQuantity = (e) => {
    if (e.code === "Minus") {
      e.preventDefault();
    }
  };

  const handleQuantityChange = (value) => {
    const newValue = +quantity + +value;
    if (newValue > 0) {
      setQuantity(newValue);
    }
  };

  const setDefaults = (product) => {
    const defaultSize = product.variants?.size?.field?.options?.sort((o1, o2) => +o1.pricing.amount - +o2.pricing.amount)[0];
    const defaultVariant = { size: defaultSize };

    if (product.variants?.size?.field?.options?.length === 1) {
      setVariant(defaultVariant);
    }

    setQuantity(1);
  };

  const validate = () => {
    let arr = [];
    if (product.variants?.flavor && !variant.flavor) {
      arr.push("Missing flavor");
    }
    if (product.variants?.filling && !variant.filling) {
      arr.push("Missing filling");
    }

    const maxQuantity = getMaxQuantity();
    if (quantity > maxQuantity) {
      arr.push("Maximum quantity available is " + maxQuantity);
    }

    return arr;
  };

  const addToCart = (e) => {
    e.preventDefault();

    e.preventDefault();
    const arr = validate();
    setErrors(arr);

    if (arr.length === 0) {
      let utm = getDataFromLocalStorage("utm", 7 * 24 * 60 * 60 * 1000);

      let item = {
        productId,
        type: product.type,
        category: product.category,
        images: [product.images[0]],
        title: product.title,
        price: getItemBasePrice(variant),
        quantity,
        variant,
        total: variantPrice * quantity,
        rules: product.rules
      };
      if (variant.photo && variant.photo.value) {
        if (Array.isArray(variant.photo.value)) {
          item.images = [...variant.photo.value, ...product.images];
        }
      }
      const cartId = cart?.id || "";

      bkstApi.post(`/site/${siteConfig.id}/v2/cart`, { item, cartId, utm }).then((res) => {
        updateCartState(res.data.cartId, quantity);
        setDefaults(product);

        navigate(`/shop`);
      });
    }
  };

  const getMaxQuantity = () => {
    let maxQuantity = 99;
    if (product.inventory) {
      const date = sessionStorage.getItem("cart.date");
      if (date) {
        const inventoryByDate = product.inventory[date];
        if (inventoryByDate) {
          maxQuantity = +inventoryByDate.threshold - +inventoryByDate.sold;
        }
      }
    }

    return maxQuantity;
  };

  // SEO metadata generation
  const generateMetadata = () => {
    if (!product) return null;

    const metadata = getItemMetadata(product);
    const canonicalUrl = getItemUrl(product);

    const basePrice = currency(metadata.price.min).format();
    const maxPrice = metadata.price.max > metadata.price.min ? ` - ${currency(metadata.price.max).format()}` : "";

    return {
      title: `${product.title} | ${siteConfig.biz}`,
      description: `${product.description?.slice(0, 155)}... Starting at ${basePrice}${maxPrice}. ${
        metadata.dietary?.length ? `${metadata.dietary.join(", ")} options available.` : ""
      }`,
      schema: {
        "@context": "https://schema.org/",
        "@type": "Product",
        name: product.title,
        image: product.images?.[0],
        description: product.description,
        offers: {
          "@type": "AggregateOffer",
          lowPrice: metadata.price.min,
          highPrice: metadata.price.max,
          priceCurrency: "USD",
          availability: product.status === "DRAFT" ? "https://schema.org/OutOfStock" : "https://schema.org/InStock"
        }
      }
    };
  };

  if (loading) {
    return <LinearProgress />;
  } else {
    const metadata = getItemMetadata(product);
    const seoData = generateMetadata();

    const numVariantOptions = (product.variants && Object.keys(product.variants)?.length) || 0;
    const numSizeOptions = (product.variants && product.variants?.size?.field?.options?.length) || 0;

    const maxQuantity = getMaxQuantity(product);

    return (
      <>
        {seoData && (
          <Helmet>
            <title>{seoData.title}</title>
            <meta name="description" content={seoData.description} />

            {/* Open Graph tags */}
            <meta property="og:title" content={seoData.title} />
            <meta property="og:description" content={seoData.description} />
            <meta property="og:image" content={product.images?.[0]} />
            <meta property="og:type" content="product" />
            <meta property="og:url" content={window.location.href} />

            {/* Twitter Card tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={seoData.title} />
            <meta name="twitter:description" content={seoData.description} />
            <meta name="twitter:image" content={product.images?.[0]} />

            {/* Schema.org JSON-LD */}
            <script type="application/ld+json">{JSON.stringify(seoData.schema)}</script>
          </Helmet>
        )}
        <Box>
          <Container maxWidth="lg">
            <Box pb={20}>
              <form onSubmit={addToCart}>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <Box pt={2} sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
                      <Link
                        underline="hover"
                        sx={{ fontSize: "0.85rem", color: "#888", fontWeight: "600", cursor: "pointer" }}
                        color="inherit"
                        onClick={() => navigate(-1)}
                      >
                        {`< BACK`}
                      </Link>
                      <span style={{ fontSize: "0.75rem", color: "#888" }}>{` | `}</span>
                      <Breadcrumbs aria-label="breadcrumb" separator="›" sx={{ fontSize: "0.95rem", color: "#888" }}>
                        <Link size="small" color="inherit" component={RouterLink} to="/shop">
                          Online Shop
                        </Link>
                        <Link
                          color="inherit"
                          component={RouterLink}
                          to={`/shop#${(product.category?.length > 0 ? product.category[0] : product.type).replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          {product.type}
                        </Link>
                      </Breadcrumbs>
                    </Box>
                  </Grid>
                  <Grid item md={7} sm={6} xs={12}>
                    <Box
                      sx={{
                        backgroundColor: "#ECECEC",
                        position: "-webkit-sticky",
                        position: "sticky",
                        top: "0"
                      }}
                    >
                      <ImageCarousel images={product.images} style={{ width: "100%", borderRadius: "4px" }} />
                    </Box>
                  </Grid>
                  <Grid item md={5} sm={6} xs={12}>
                    <Box>
                      <Box>
                        <Box mb={1}>
                          <Typography variant="h3" gutterBottom>
                            {product.title}
                          </Typography>
                        </Box>
                        <Box>
                          {numSizeOptions === 1 && <Chip size="small" label={`Size | ${product.variants?.size?.field?.options?.[0]?.name}`} />}
                          {metadata.leadTime > 0 && <Chip size="small" label={`Pre-order (min ${metadata.leadTime} hour notice)`} />}
                          {metadata.dietary?.length > 0 && <Chip size="small" label={`${metadata.dietary.join(", ")} options`} />}
                        </Box>
                        <Box my={1}>
                          <Typography variant="body1">
                            {currency(metadata.price.min).format()}{" "}
                            {metadata.price.max > metadata.price.min && `- ${currency(metadata.price.max).format()}`}
                          </Typography>
                        </Box>
                        {metadata.dateRange && (
                          <Box my={1}>
                            <Alert severity="info">{metadata.dateRange} only!</Alert>
                          </Box>
                        )}
                        {metadata.daysOfWeek && (
                          <Box my={1}>
                            <Alert severity="info">{metadata.daysOfWeek}</Alert>
                          </Box>
                        )}
                        {(product.description || product.ingredients) && (
                          <Box my={1}>
                            <ProductDescription description={`${product.description} ${product.ingredients ? `<br><br>Ingredients: ${product.ingredients}` : ""}`} />
                          </Box>
                        )}
                      </Box>
                      {variants && (numVariantOptions > 1 || numSizeOptions > 1) && (
                        <Box my={3}>
                          <Paper elevation={0}>
                            <Box px={2} py={2}>
                              <Box mb={4}>
                                <Typography variant="h5" color="textSecondary">
                                  Select Options
                                </Typography>
                              </Box>
                              <ProductVariants
                                variant={variant}
                                fields={variants}
                                onChange={(o) => {
                                  if (o.size?.name !== variant.size?.name) {
                                    setVariant({ size: o.size });
                                  } else {
                                    setVariant(o);
                                  }
                                }}
                              />
                            </Box>
                          </Paper>
                        </Box>
                      )}
                      {product.status === "DRAFT" && (
                        <Box my={3}>
                          <Alert severity="error">Sorry, this item is no longer available!</Alert>
                        </Box>
                      )}
                      <Box
                        my={3}
                        sx={{
                          backgroundColor: "#ECECEC",
                          position: "-webkit-sticky",
                          position: "sticky",
                          bottom: "0",
                          opacity: "1",
                          zIndex: 1000,
                          borderRadius: "5px"
                        }}
                      >
                        {errors && errors.length > 0 && (
                          <Box my={1}>
                            <Alert severity="error">{errors.join(". ")}</Alert>
                          </Box>
                        )}
                        <Box px={2} pb={2} pt={3} sx={{ display: "flex", gap: "10px" }}>
                          <Box>
                            <TextField
                              type="number"
                              name="quantity"
                              label="Quantity"
                              value={quantity}
                              variant="outlined"
                              id="quantity"
                              error={quantity > maxQuantity}
                              helperText={quantity > maxQuantity ? `Max ${maxQuantity} available` : ``}
                              onChange={(e) => {
                                setQuantity(e.target.value);
                              }}
                              inputProps={{
                                min: 1,
                                max: maxQuantity,
                                sx: { textAlign: "center" }
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <IconButton onClick={() => handleQuantityChange(-1)} disabled={quantity === 1}>
                                      <RemoveIcon />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton onClick={() => handleQuantityChange(1)} disabled={quantity >= maxQuantity}>
                                      <AddIcon />
                                    </IconButton>
                                  </InputAdornment>
                                )
                              }}
                              sx={{
                                width: "125px",
                                "& .MuiOutlinedInput-root": {
                                  paddingRight: 0,
                                  paddingLeft: 0
                                },
                                backgroundColor: "#FFFFFF"
                              }}
                              onKeyPress={validateQuantity}
                            />
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Button
                              fullWidth
                              size="large"
                              variant="contained"
                              color="primary"
                              type="submit"
                              disabled={product.status === "DRAFT" || quantity > maxQuantity}
                              sx={{ height: "56px" }}
                            >
                              {+variantPrice > 0 && `${currency(variantPrice * quantity).format()} | `} Add to Cart
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Container>
        </Box>
      </>
    );
  }
}
